import i18next from 'i18next';
import { z } from 'zod';
import { makeZodI18nMap } from 'zod-i18n-map';
import deZod from 'zod-i18n-map/locales/de/zod.json';
import enZod from 'zod-i18n-map/locales/en/zod.json';
import esZod from 'zod-i18n-map/locales/es/zod.json';
import frZod from 'zod-i18n-map/locales/fr/zod.json';
import ptZod from 'zod-i18n-map/locales/pt/zod.json';

/** App message locales that have a matching zod-i18n-map translation bundle. */
const ZOD_I18N_LOCALES = ['en', 'es', 'fr', 'pt', 'de'] as const;

type ZodI18nLocale = (typeof ZOD_I18N_LOCALES)[number];

const FALLBACK_ZOD_LOCALE: ZodI18nLocale = 'es';

let i18nInitialized = false;

/**
 * Maps the app locale (e.g. es-AR, en-US) to a zod-i18n-map language code.
 */
function resolveZodLocale(appLocale: string): ZodI18nLocale {
  const language = appLocale.substring(0, 2).toLowerCase();

  if ((ZOD_I18N_LOCALES as readonly string[]).includes(language)) {
    return language as ZodI18nLocale;
  }

  return FALLBACK_ZOD_LOCALE;
}

/**
 * Syncs Zod's global error map with the active app locale.
 * Call whenever react-intl locale changes (see LanguageProvider).
 */
export function syncZodErrorMap(appLocale: string): void {
  const zodLocale = resolveZodLocale(appLocale);

  if (!i18nInitialized) {
    void i18next.init({
      lng: zodLocale,
      fallbackLng: 'en',
      resources: {
        en: { zod: enZod },
        es: { zod: esZod },
        fr: { zod: frZod },
        pt: { zod: ptZod },
        de: { zod: deZod },
      },
      ns: ['zod'],
      defaultNS: 'zod',
      interpolation: { escapeValue: false },
    });
    z.setErrorMap(makeZodI18nMap());
    i18nInitialized = true;
    return;
  }

  void i18next.changeLanguage(zodLocale);
}

// Default before LanguageProvider mounts (app default locale is Spanish).
syncZodErrorMap(FALLBACK_ZOD_LOCALE);
